import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';
import CropFinanceReportModal from './CropFinanceReportModal';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EMPTY = {
  crop_name: '',
  crop_type: '',
  land_id: '',
  area_sown_kanals: '',
  area_sown_marlas: '',
  sowing_date: '',
  expected_harvest_date: '',
  actual_harvest_date: '',
  expected_yield_kg: '',
  actual_yield_kg: '',
  status: 'growing',
  notes: ''
};

const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };
const statusColor = { growing: '#27ae60', harvested: '#3498db', sold: '#9b59b6', failed: '#e74c3c' };

// Format area as "X kanal Y marla"
const formatArea = (kanals, marlas) => {
  const k = parseInt(kanals || 0);
  const m = parseInt(marlas || 0);
  if (k === 0 && m === 0) return '—';
  if (m === 0) return `${k} kanal`;
  if (k === 0) return `${m} marla`;
  return `${k} kanal ${m} marla`;
};

const CropsTab = () => {
  const [crops, setCrops] = useState([]);
  const [lands, setLands] = useState([]);
  const [cropNames, setCropNames] = useState([]);
  const [cropTypes, setCropTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [reportType, setReportType] = useState(null); // 'expense' | 'income' | null

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/agriculture/crops`, { headers: headers() }),
      axios.get(`${API}/agriculture/lands`, { headers: headers() }),
      axios.get(`${API}/agriculture/settings/crop_name`, { headers: headers() }),
      axios.get(`${API}/agriculture/settings/crop_type`, { headers: headers() })
    ]).then(([c, l, cn, ct]) => {
      setCrops(c.data.data);
      setLands(l.data.data);
      setCropNames(cn.data.data || []);
      setCropTypes(ct.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const fmt = d => d ? new Date(d).toLocaleDateString('en-PK') : '—';
  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      ...c,
      area_sown_kanals: c.area_sown_kanals || '',
      area_sown_marlas: c.area_sown_marlas || '',
      sowing_date: c.sowing_date?.split('T')[0] || '',
      expected_harvest_date: c.expected_harvest_date?.split('T')[0] || '',
      actual_harvest_date: c.actual_harvest_date?.split('T')[0] || ''
    });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.crop_name || !form.sowing_date) return alert('Crop name and sowing date are required');
    const k = parseInt(form.area_sown_kanals || 0);
    const m = parseInt(form.area_sown_marlas || 0);
    if (k === 0 && m === 0) return alert('Please enter area (kanal and/or marla)');
    if (m < 0 || m > 19) return alert('Marla must be between 0 and 19');
    if (k < 0) return alert('Kanal must be 0 or more');

    setSaving(true);
    try {
      const payload = { ...form, area_sown_kanals: k, area_sown_marlas: m };
      if (editId) await axios.put(`${API}/agriculture/crops/${editId}`, payload, { headers: headers() });
      else await axios.post(`${API}/agriculture/crops`, payload, { headers: headers() });
      setShowForm(false); fetchAll();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this crop?')) return;
    await axios.delete(`${API}/agriculture/crops/${id}`, { headers: headers() });
    fetchAll();
  };

  // Live preview: total decimal kanals while typing
  const previewKanals = parseInt(form.area_sown_kanals || 0) + parseInt(form.area_sown_marlas || 0) / 20;

  const filtered = filter === 'all' ? crops : crops.filter(c => c.status === filter);
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(filtered, 10);

  // Resolve current crop_name string to its setting id (used to filter crop_types in the dropdown)
  const selectedCropNameId = (() => {
    const match = cropNames.find(c => c.value === form.crop_name);
    return match ? match.id : null;
  })();

  // crop_type options filtered by currently selected crop_name
  const availableCropTypes = cropTypes.filter(t => String(t.parent_id) === String(selectedCropNameId));

  // Check if the saved values in the edit form exist in current settings
  const isCropNameInSettings = form.crop_name === '' || cropNames.some(c => c.value === form.crop_name);
  const isCropTypeInSettings = form.crop_type === '' || cropTypes.some(t => t.value === form.crop_type && String(t.parent_id) === String(selectedCropNameId));

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Crops Management</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setReportType('expense')} style={{ padding: '12px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>📄 Expense Report</button>
          <button onClick={() => setReportType('income')} style={{ padding: '12px 20px', background: '#2980b9', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>💰 Income Report</button>
          <button onClick={openAdd} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Add Crop</button>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {['all', 'growing', 'harvested', 'sold', 'failed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === f ? '#27ae60' : '#f0f0f0', color: filter === f ? 'white' : '#555'
          }}>{f.charAt(0).toUpperCase() + f.slice(1)} {f === 'all' ? `(${crops.length})` : `(${crops.filter(c => c.status === f).length})`}</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Crop', 'Type', 'Field', 'Area', 'Lease Share', 'Sowing Date', 'Expected Harvest', 'Yield (kg)', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No crops found.</td></tr>
            ) : paginatedData.map((c, i) => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px 16px', fontWeight: '600', color: '#2c3e50' }}>{c.crop_name}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{c.crop_type || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{c.field_name || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555', fontWeight: '600' }}>{formatArea(c.area_sown_kanals, c.area_sown_marlas)}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>
                  {parseFloat(c.lease_share || 0) > 0
                    ? `Rs. ${parseFloat(c.lease_share).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                    : '—'}
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{fmt(c.sowing_date)}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{fmt(c.expected_harvest_date)}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{c.actual_yield_kg || c.expected_yield_kg || '—'}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: `${statusColor[c.status]}20`, color: statusColor[c.status] }}>{c.status}</span>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEdit(c)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                    <button onClick={() => handleDelete(c.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalRecords={filtered.length}
          rowsPerPage={10}
        />
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Crop' : 'Add New Crop'}</h3>

            {/* Empty settings warning */}
            {cropNames.length === 0 && (
              <div style={{ padding: '14px 18px', background: '#fff3cd', borderRadius: '10px', borderLeft: '4px solid #f39c12', fontSize: '13px', color: '#7f5b00', marginBottom: '20px' }}>
                ⚠️ No crops added in Settings yet. Please go to <strong>⚙️ Settings → 🌾 Crops</strong> first and add at least one crop.
              </div>
            )}

            {/* Old-data warning when editing a crop with messy name */}
            {editId && (!isCropNameInSettings || (form.crop_type && !isCropTypeInSettings)) && (
              <div style={{ padding: '14px 18px', background: '#e8f4fd', borderRadius: '10px', borderLeft: '4px solid #3498db', fontSize: '13px', color: '#1a5490', marginBottom: '20px' }}>
                💡 This crop was added before the dropdown system. Current values:
                <div style={{ marginTop: '6px' }}>
                  • Crop: <strong>{form.crop_name}</strong>{!isCropNameInSettings && <span style={{ color: '#e74c3c' }}> (not in Settings)</span>}<br/>
                  {form.crop_type && (
                    <>• Type: <strong>{form.crop_type}</strong>{!isCropTypeInSettings && <span style={{ color: '#e74c3c' }}> (not in Settings)</span>}</>
                  )}
                </div>
                <div style={{ marginTop: '6px', fontSize: '12px' }}>
                  You can keep them as-is or pick from the dropdown to update.
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Crop Name *</label>
                <select
                  style={inputStyle}
                  value={isCropNameInSettings ? form.crop_name : ''}
                  onChange={e => setForm({ ...form, crop_name: e.target.value, crop_type: '' })}
                  disabled={cropNames.length === 0}
                >
                  <option value="">-- Select Crop --</option>
                  {/* If editing an old crop with a name NOT in settings, show it as a disabled option for context */}
                  {editId && !isCropNameInSettings && form.crop_name && (
                    <option value={form.crop_name} disabled>
                      {form.crop_name} (old, not in Settings)
                    </option>
                  )}
                  {cropNames.map(c => <option key={c.id} value={c.value}>{c.value}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Crop Type / Variety</label>
                <select
                  style={inputStyle}
                  value={isCropTypeInSettings ? form.crop_type : ''}
                  onChange={e => setForm({ ...form, crop_type: e.target.value })}
                  disabled={!selectedCropNameId || availableCropTypes.length === 0}
                >
                  <option value="">
                    {!selectedCropNameId
                      ? '-- Select Crop first --'
                      : availableCropTypes.length === 0
                        ? '-- No types for this crop --'
                        : '-- Select Type --'}
                  </option>
                  {availableCropTypes.map(t => <option key={t.id} value={t.value}>{t.value}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Field</label>
                <select style={inputStyle} value={form.land_id} onChange={e => setForm({ ...form, land_id: e.target.value })}>
                  <option value="">-- Select Field --</option>
                  {lands.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.field_name} ({l.total_kanals || 0} kanals, {parseFloat(l.area_acres || 0).toFixed(2)} acres)
                    </option>
                  ))}
                </select>
              </div>

              {/* Kanal + Marla inputs side by side */}
              <div>
                <label style={labelStyle}>Area Sown — Kanal *</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  value={form.area_sown_kanals}
                  onChange={e => setForm({ ...form, area_sown_kanals: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <label style={labelStyle}>Area Sown — Marla *</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  max="19"
                  value={form.area_sown_marlas}
                  onChange={e => setForm({ ...form, area_sown_marlas: e.target.value })}
                  placeholder="0"
                />
                <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>0–19 (20 marla = 1 kanal)</div>
              </div>

              {/* Live area preview */}
              {(form.area_sown_kanals || form.area_sown_marlas) ? (
                <div style={{ gridColumn: '1/-1', padding: '10px 14px', background: '#e8f5e9', borderRadius: '8px', fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>
                  📐 Total area: {formatArea(form.area_sown_kanals, form.area_sown_marlas)}
                  {previewKanals > 0 && ` (${previewKanals.toFixed(3)} kanals)`}
                </div>
              ) : null}

              <div>
                <label style={labelStyle}>Sowing Date *</label>
                <input style={inputStyle} type="date" value={form.sowing_date} onChange={e => setForm({ ...form, sowing_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Expected Harvest Date</label>
                <input style={inputStyle} type="date" value={form.expected_harvest_date} onChange={e => setForm({ ...form, expected_harvest_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Expected Yield (kg)</label>
                <input style={inputStyle} type="number" value={form.expected_yield_kg} onChange={e => setForm({ ...form, expected_yield_kg: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Actual Harvest Date</label>
                <input style={inputStyle} type="date" value={form.actual_harvest_date} onChange={e => setForm({ ...form, actual_harvest_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Actual Yield (kg)</label>
                <input style={inputStyle} type="number" value={form.actual_yield_kg} onChange={e => setForm({ ...form, actual_yield_kg: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="growing">Growing</option>
                  <option value="harvested">Harvested</option>
                  <option value="sold">Sold</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving || cropNames.length === 0} style={{ padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', opacity: cropNames.length === 0 ? 0.5 : 1 }}>
                {saving ? 'Saving...' : editId ? 'Update Crop' : 'Add Crop'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reportType && (
        <CropFinanceReportModal
          type={reportType}
          crops={crops}
          onClose={() => setReportType(null)}
        />
      )}
    </div>
  );
};

export default CropsTab;